import { MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { EmptyState, cn } from '@/features/issue'
import { useSession } from '@/lib/session'
import { useWorkspace } from '@/lib/workspace'

import { ChannelList } from './ChannelList'
import { Conversation } from './Conversation'
import { NewConversationModal } from './NewConversationModal'
import { useChannel, useChannels, useChatRealtime } from './hooks'

/**
 * `/{slug}/chat` and `/{slug}/chat/:channelId`.
 *
 * Two panes from the medium breakpoint up; one at a time below it, with the
 * conversation taking over the screen and a back button returning to the
 * list. The active conversation lives in the URL so it is linkable and the
 * notification bell can deep-link into it.
 */
export default function ChatPage() {
  const workspace = useWorkspace()
  const { user } = useSession()
  const navigate = useNavigate()
  const { channelId = null } = useParams<{ channelId: string }>()
  const basePath = workspace.path('/chat')

  const channels = useChannels(workspace.slug)
  const { channel, isLoading: channelLoading, error: channelError } = useChannel(workspace.slug, channelId)
  const [creating, setCreating] = useState(false)

  useChatRealtime(workspace.slug, (deletedId) => {
    if (deletedId === channelId) navigate(basePath, { replace: true })
  })

  if (!user) return null
  const isAdmin = workspace.role === 'owner' || workspace.role === 'admin'
  const showList = !channelId

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] min-h-0">
      <aside
        className={cn(
          'w-full shrink-0 border-r border-ink-200 bg-white md:block md:w-80 dark:border-ink-800 dark:bg-ink-950',
          showList ? 'block' : 'hidden',
        )}
      >
        <ChannelList
          channels={channels.data?.data ?? []}
          isLoading={channels.isLoading}
          error={channels.error}
          onRetry={() => void channels.refetch()}
          activeId={channelId}
          basePath={basePath}
          onNew={() => setCreating(true)}
        />
      </aside>

      <section className={cn('min-w-0 flex-1', showList ? 'hidden md:block' : 'block')} aria-label="Conversation">
        {!channelId ? (
          <EmptyState
            title="Pick a conversation"
            description="Choose a teammate or group on the left, or start a new one."
            action={
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                <MessageSquare className="size-4" aria-hidden="true" />
                New message
              </button>
            }
          />
        ) : channel ? (
          <Conversation
            slug={workspace.slug}
            channel={channel}
            currentUserId={user.id}
            isAdmin={isAdmin}
            onBack={() => navigate(basePath)}
            onLeft={() => navigate(basePath, { replace: true })}
          />
        ) : channelLoading ? null : (
          <EmptyState
            title="Conversation not found"
            description={channelError ? 'It may have been deleted, or you are no longer a member.' : undefined}
            action={
              <button type="button" onClick={() => navigate(basePath)} className="text-sm text-brand-700 underline-offset-2 hover:underline">
                Back to chat
              </button>
            }
          />
        )}
      </section>

      <NewConversationModal
        open={creating}
        onClose={() => setCreating(false)}
        slug={workspace.slug}
        currentUserId={user.id}
        onCreated={(created) => {
          setCreating(false)
          navigate(`${basePath}/${created.id}`)
        }}
      />
    </div>
  )
}
